import React, { useMemo, type MutableRefObject } from 'react';
import isEqual from 'lodash/isEqual';
import type { VirtualItem, VirtualizerOptions } from '@tanstack/virtual-core';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isShallowEqual } from '@atlassian/jira-platform-shallow-equal/src/index.tsx';
import { ROW_HEIGHT } from '../../../common/constants.tsx';
import { useScrollElement } from '../../../controllers/scroll-state/index.tsx';
import { useSSRSafeVirtualizer } from '../../../controllers/virtualization/index.tsx';
import {
	useVirtualizationActions,
	type VirtualItemsMap,
} from '../../../controllers/virtualization/rss-store.tsx';

export type Props = {
	rows: number;
	// make setTableHeight required and remove totalSizeRef when cleaning up jira_list_virtualizer_table_height
	totalSizeRef: MutableRefObject<number>;
	getItemKey: (index: number) => number | string | bigint;
	options?: Partial<VirtualizerOptions<HTMLElement, Element>>;
};

const estimateSize = () => ROW_HEIGHT + 1; // include border

const InitializeVirtualizerInner = ({
	rows = 0,
	getItemKey,
	totalSizeRef,
	options: virtualizerOptions,
}: Props) => {
	const scrollElement = useScrollElement();
	const { setVirtualizer, setVirtualItemsMap } = useVirtualizationActions();

	const options = useMemo(
		() => ({
			count: rows,
			getScrollElement: () => scrollElement,
			estimateSize,
			getItemKey,
			overscan: 10,
			...(fg('jira_spreadsheet_timeline_image_export') && virtualizerOptions),
		}),
		[getItemKey, rows, scrollElement, virtualizerOptions],
	);

	const virtualizer = useSSRSafeVirtualizer(options);
	const totalSize = virtualizer?.getTotalSize();
	const items = virtualizer?.getVirtualItems();

	// eslint-disable-next-line no-param-reassign, react-hooks/react-compiler
	totalSizeRef.current = totalSize ?? 0;

	const prevVirtualizer = usePrevious(virtualizer);
	const prevItems = usePrevious(items);

	if (!isShallowEqual(prevVirtualizer, virtualizer)) {
		setVirtualizer(virtualizer);
	}

	if (!isEqual(prevItems, items)) {
		const virtualItemsMap = items?.reduce((acc: VirtualItemsMap, item: VirtualItem) => {
			acc[String(item.key)] = true;
			return acc;
		}, {});

		setVirtualItemsMap(virtualItemsMap);
	}

	return null;
};

export const InitializeVirtualizer = (props: Props) => {
	// Skip any attempts to perform virtualization in SSR
	if (__SERVER__) {
		return null;
	}

	return <InitializeVirtualizerInner {...props} />;
};
